import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Plan, UserPlan, useBatchUploadQuery, useCreateBatchPaymentMutation, useUserQuery } from '../../../../api/gql';
import { BillingBox } from '../../../../components/BillingBox';
import { BillingPlans } from '../../../../components/BillingPlans';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { PageHeader } from '../../../../components/PageHeader';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../../layout';
import { PaymentModal } from '../../../../modules/payment/PaymentModal';
import { notify } from '../../../../utils/notification';
import { fromCentesimal } from '../../../../utils/transform';

const TRANS_NS = 'billing';

export const BatchBilling = () => {
  const { batchId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();
  const [clientPaymentSecret, setClientPaymentSecret] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const handleQueryError = useCallback(
    (error: unknown) => {
      console.error(error);
      notify.negative({ message: t('query.error') });
      safeNavigate(-1);
    },
    [safeNavigate, t],
  );

  const handleMutationError = (error: unknown) => {
    console.error(error);
    notify.negative({ message: t('mutation.error') });
  };

  const { data: userData, isLoading: isLoadingUserQuery, error } = useUserQuery();
  const { plan: userPlan, remindersCount = 0, remindersLimit = null } = userData?.user || {};

  const {
    data: batchData,
    isLoading: isLoadingBatchQuery,
    error: batchQueryError,
  } = useBatchUploadQuery({
    id: batchId ?? '',
  });

  const invoicesCount = batchData?.batchUpload?.invoicesCount ?? 0;
  const remindingsLeft =
    remindersLimit !== null ? Math.max(0, remindersLimit - remindersCount) : Number.POSITIVE_INFINITY;

  useEffect(() => {
    if (error || batchQueryError) {
      console.error(error ?? batchQueryError);
      handleQueryError(error || batchQueryError);
    }
  }, [batchQueryError, error, handleQueryError]);

  const { mutate: createPaymentMutate, isPending: isMutating } = useCreateBatchPaymentMutation({
    onSuccess: ({ createBatchPayment: { paymentRequired, clientSecret } }) =>
      handleSuccess(paymentRequired, clientSecret),
    onError: handleMutationError,
  });

  const handleSuccess = (paymentRequired: boolean, clientSecret?: string | null) => {
    if (paymentRequired && clientSecret) {
      setClientPaymentSecret(clientSecret);
      setPaymentModalOpen(true);
      return;
    }
    notify.positive({ message: t('mutation.success') });
    safeNavigate(`/`, { replace: true });
  };

  const onPlanChoose = (plan: Plan) => {
    if (!batchId) {
      throw new Error('');
    }
    if (clientPaymentSecret) {
      setPaymentModalOpen(true);
      return;
    }
    createPaymentMutate({ id: batchId, plan: plan });
  };

  const isLoading = useMemo(() => isLoadingBatchQuery || isLoadingUserQuery, [isLoadingUserQuery, isLoadingBatchQuery]);

  return (
    <PageLayout>
      <PageHeader title={t('title')} subtitle={userPlan === UserPlan.Trial ? t('subtitle') : t('planSubtitle')} />
      <PageContent
        sx={{
          px: { md: 4, xs: 0 },
          pt: { xs: 4, md: 8 },
          alignItems: 'center',
        }}
      >
        {isLoading ? <FullSizeLoader /> : null}

        {batchData && userPlan === UserPlan.Trial && (
          <BillingPlans
            user={userData?.user}
            scenarioPlan={batchData.batchUpload.scenario?.plan}
            onPlanChoose={onPlanChoose}
            standardDiscount={fromCentesimal(batchData.batchUpload.standardDiscount ?? 0)}
            standardPrice={fromCentesimal(batchData.batchUpload.standardPrice ?? 0)}
            isTrialPlanDisplayed={false}
          />
        )}

        {batchData && (userPlan === UserPlan.Optimal || userPlan === UserPlan.Complete) && (
          <Box display={'flex'} flex={'1 1 0'} alignItems={'flex-start'} justifyContent={'center'} height={'100%'}>
            <BillingBox
              isBatch
              userPlan={userPlan}
              remindingsLeft={remindingsLeft}
              invoicesCount={invoicesCount}
              handleSubmit={() => onPlanChoose(Plan.Standard)}
              isLoading={isMutating}
            />
          </Box>
        )}
      </PageContent>

      <PaymentModal
        returnUrl={`${window.location.origin}/upload/batch/${batchId ?? ''}/payment-result`}
        clientSecret={clientPaymentSecret}
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
      />
    </PageLayout>
  );
};
